import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { Lightning, ChartLineUp, Shield, Brain, Notebook, Pulse, Sparkle, ArrowRight, Check } from "@phosphor-icons/react";
import { Seo } from "@/components/Seo";
import { InstallAppButton } from "@/components/InstallAppButton";

const LANDING_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "TradeMind AI",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web, Android",
  url: "https://trademind.cz",
  description:
    "AI mentor pro tradery: analýza grafů ze screenshotu, obchodní deník, risk management a Prop Firm Guardian pro prop trading firmy.",
  inLanguage: "cs",
  offers: [
    { "@type": "Offer", name: "FREE", price: "0", priceCurrency: "CZK" },
    { "@type": "Offer", name: "PREMIUM", price: "499", priceCurrency: "CZK" },
    { "@type": "Offer", name: "ELITE", price: "1290", priceCurrency: "CZK" },
  ],
  publisher: { "@type": "Organization", name: "VV Software", address: { "@type": "PostalAddress", streetAddress: "Nové sady 988/2", postalCode: "602 00", addressLocality: "Brno", addressCountry: "CZ" } },
};

const NavBar = () => {
  const { t, lang, setLang } = useT();
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 bg-zinc-950/70 backdrop-blur-xl border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <img src="/icons/icon-192.png" alt="TradeMind AI" className="w-8 h-8 rounded-md" />
          <span className="hidden min-[480px]:inline font-semibold tracking-tight text-zinc-50 whitespace-nowrap">TradeMind <span className="text-blue-500">AI</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
          <a href="#features" className="hover:text-zinc-100 transition-colors" data-testid="nav-features">{t("nav.features")}</a>
          <a href="#pricing" className="hover:text-zinc-100 transition-colors" data-testid="nav-pricing">{t("nav.pricing")}</a>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider border border-zinc-800 rounded-md overflow-hidden">
            <button data-testid="lang-cs" onClick={() => setLang("cs")} className={`px-1.5 sm:px-2 py-1 transition-colors ${lang === "cs" ? "bg-zinc-100 text-zinc-900" : "text-zinc-500 hover:text-zinc-200"}`}>CS</button>
            <button data-testid="lang-en" onClick={() => setLang("en")} className={`px-1.5 sm:px-2 py-1 transition-colors ${lang === "en" ? "bg-zinc-100 text-zinc-900" : "text-zinc-500 hover:text-zinc-200"}`}>EN</button>
          </div>
          {user ? (
            <Link to="/app" data-testid="nav-dashboard" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 sm:px-4 py-2 rounded-md text-sm transition-colors whitespace-nowrap">{t("nav.dashboard")}</Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-zinc-300 hover:text-white text-sm transition-colors px-2 sm:px-3 py-2 whitespace-nowrap">{t("nav.login")}</Link>
              <Link to="/register" data-testid="nav-register" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 sm:px-4 py-2 rounded-md text-sm transition-colors whitespace-nowrap">{t("nav.register")}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const StatRow = () => (
  <div className="border-y border-zinc-800 bg-zinc-950/50 backdrop-blur">
    <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
      {[
        ["A+", "AVG SETUP GRADE"],
        ["<3s", "ANALYSIS LATENCY"],
        ["PROP FIRM", "CHALLENGE SUPPORT"],
        ["24/7", "AI MENTOR"],
      ].map(([v, l]) => (
        <div key={l}>
          <div className="text-2xl font-mono font-medium tracking-tight text-zinc-100">{v}</div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</div>
        </div>
      ))}
    </div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, desc, accent = "blue" }) => (
  <div className="group bg-zinc-950 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-all duration-200">
    <div className={`w-10 h-10 rounded-lg bg-${accent}-500/10 border border-${accent}-500/20 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform`}>
      <Icon weight="bold" size={20} className={`text-${accent}-400`} />
    </div>
    <h3 className="text-lg font-medium text-zinc-100 mb-2">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
  </div>
);

const PricingCard = ({ name, price, currency = "Kč", period, desc, features, cta, highlight, testid }) => (
  <div data-testid={testid} className={`relative rounded-xl p-8 border ${highlight ? "border-blue-500/50 bg-gradient-to-b from-blue-500/5 to-zinc-950" : "border-zinc-800 bg-zinc-950"}`}>
    {highlight && (
      <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-blue-600 text-xs font-mono uppercase tracking-wider text-white">Most popular</div>
    )}
    <div className="text-sm font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">{name}</div>
    <div className="flex items-baseline gap-1 mb-2">
      <span className="text-4xl font-semibold text-zinc-50 tracking-tight">{price}</span>
      <span className="text-base text-zinc-300">{currency}</span>
      <span className="text-sm text-zinc-500">{period}</span>
    </div>
    <p className="text-sm text-zinc-400 mb-6">{desc}</p>
    <ul className="space-y-3 mb-8">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
          <Check weight="bold" size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <Link to={highlight ? "/app/upgrade" : "/register"} data-testid={`${testid}-cta`} className={`block w-full text-center px-4 py-2.5 rounded-md font-medium text-sm transition-colors ${highlight ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border border-zinc-800"}`}>{cta}</Link>
  </div>
);

const Landing = () => {
  const { t, lang } = useT();
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 overflow-x-hidden" style={{ fontFamily: "Manrope, sans-serif" }}>
      <NavBar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full bg-blue-600/10 blur-[120px]" />
        </div>
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-16 sm:pt-20 sm:pb-24 lg:pt-32 lg:pb-40">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-zinc-800 bg-zinc-900/50 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {t("landing.tag")}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-semibold text-zinc-50 max-w-4xl leading-[1.05]" style={{ fontFamily: "Outfit, sans-serif" }}>
            {t("landing.h1")}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            {t("landing.sub")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/register" data-testid="hero-cta-primary" className="group bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-md transition-colors flex items-center gap-2">
              {t("landing.cta")}
              <ArrowRight weight="bold" size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="#features" data-testid="hero-cta-secondary" className="border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900 text-zinc-200 font-medium px-6 py-3 rounded-md transition-all">
              {t("landing.cta2")}
            </a>
            <InstallAppButton lang={lang} variant="gold" />
          </div>

          {/* Mock dashboard preview */}
          <div className="mt-16 relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 via-transparent to-blue-600/20 blur-2xl opacity-50" />
            <div className="relative bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden shadow-2xl">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-700"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-700"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-700"></div>
                </div>
                <div className="ml-4 text-xs font-mono text-zinc-500">trademind.ai/dashboard</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-px bg-zinc-800/50">
                <div className="sm:col-span-3 bg-zinc-950 p-5 sm:p-6 space-y-4 sm:min-h-[320px]">
                  <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">XAUUSD · H1</div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Signal</div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono font-medium" style={{ filter: "drop-shadow(0 0 8px rgba(245,158,11,0.3))" }}>
                      <ChartLineUp weight="bold" size={14} /> {lang === "cs" ? "POČKAT" : "WAIT"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Confidence</div>
                    <div className="text-2xl font-mono font-medium text-zinc-100">58<span className="text-zinc-500">%</span></div>
                  </div>
                  <div className="pt-2 border-t border-zinc-800">
                    <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5">AI</div>
                    <p className="text-xs text-zinc-300 leading-relaxed">
                      {lang === "cs"
                        ? "Trh v korekci — počkej na potvrzení. Hlídám setup za tebe."
                        : "Market in correction — wait for confirmation. I'm watching the setup for you."}
                    </p>
                  </div>
                </div>
                <div className="sm:col-span-9 bg-zinc-950 p-4 relative min-h-[220px] sm:min-h-[320px]">
                  <img src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85" alt="chart" className="w-full h-full object-cover rounded opacity-80" />
                  <div className="absolute top-6 right-6 flex flex-col gap-2 items-end">
                    <div className="text-xs font-mono text-zinc-300 bg-zinc-950/80 backdrop-blur px-2 py-1 rounded border border-zinc-700">H1 · INTRADAY</div>
                    <div className="text-xs font-mono text-amber-400 bg-zinc-950/80 backdrop-blur px-2 py-1 rounded border border-amber-500/30">C SETUP</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo banner */}
      <section className="max-w-7xl mx-auto px-6 pt-10">
        <div className="rounded-lg border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-yellow-600/5 to-amber-500/10 px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="promo-banner">
          <p className="text-sm text-zinc-200">
            <Sparkle weight="fill" size={16} className="inline text-amber-400 mr-2" />
            {lang === "cs"
              ? <>Začni s <strong className="text-amber-400">10% slevou na 3 měsíce</strong> — použij kód <strong className="font-mono text-amber-400">START10</strong> při prvním předplatném.</>
              : <>Start with a <strong className="text-amber-400">10% discount for 3 months</strong> — use code <strong className="font-mono text-amber-400">START10</strong> with your first subscription.</>}
          </p>
          <Link to="/register" data-testid="promo-banner-cta" className="whitespace-nowrap text-sm font-medium px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-zinc-950 transition-colors">
            {lang === "cs" ? "Využít slevu" : "Claim discount"}
          </Link>
        </div>
      </section>

      <StatRow />

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-400 mb-3">// {lang === "cs" ? "FUNKCE" : "FEATURES"}</div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-50" style={{ fontFamily: "Outfit, sans-serif" }}>
            {lang === "cs" ? "Vše, co potřebuješ pro profesionální obchodování." : "Everything you need to trade like a pro."}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard icon={ChartLineUp} accent="blue" title={t("landing.feat1.title")} desc={t("landing.feat1.desc")} />
          <FeatureCard icon={Shield} accent="green" title={t("landing.feat2.title")} desc={t("landing.feat2.desc")} />
          <FeatureCard icon={Brain} accent="blue" title={t("landing.feat3.title")} desc={t("landing.feat3.desc")} />
          <FeatureCard icon={Notebook} accent="amber" title={t("landing.feat4.title")} desc={t("landing.feat4.desc")} />
          <FeatureCard icon={Pulse} accent="blue" title={t("landing.feat5.title")} desc={t("landing.feat5.desc")} />
          <FeatureCard icon={Sparkle} accent="amber" title={t("landing.feat6.title")} desc={t("landing.feat6.desc")} />
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24 border-t border-zinc-800">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-400 mb-3">// {lang === "cs" ? "CENÍK" : "PRICING"}</div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-50" style={{ fontFamily: "Outfit, sans-serif" }}>
            {t("landing.pricing.title")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <PricingCard testid="plan-free" name="FREE" price="0" currency="Kč" period={lang === "cs" ? "/měsíc" : "/month"} desc={t("landing.pricing.free")} features={[
            lang === "cs" ? "3 AI analýzy denně" : "3 AI analyses per day",
            lang === "cs" ? "Risk kalkulačka" : "Risk calculator",
            lang === "cs" ? "Základní dashboard" : "Basic dashboard",
          ]} cta={t("landing.cta.free")} />
          <PricingCard testid="plan-premium" name="PREMIUM" price="499" currency="Kč" period={lang === "cs" ? "/měsíc" : "/month"} desc={t("landing.pricing.pro")} highlight features={[
            lang === "cs" ? "30 AI analýz denně" : "30 AI analyses per day",
            lang === "cs" ? "Trading deník + statistiky" : "Trading journal + stats",
            lang === "cs" ? "AI Mentor 24/7" : "AI Mentor 24/7",
            lang === "cs" ? "AI scoring (A+ až F)" : "AI scoring (A+ to F)",
          ]} cta={t("landing.cta.upgrade")} />
          <PricingCard testid="plan-elite" name="ELITE" price="1290" currency="Kč" period={lang === "cs" ? "/měsíc" : "/month"} desc={t("landing.pricing.elite")} features={[
            lang === "cs" ? "100 AI analýz denně" : "100 AI analyses per day",
            lang === "cs" ? "Vše z PREMIUM" : "Everything in PREMIUM",
            "Prop Firm Guardian",
            lang === "cs" ? "Challenge Mode" : "Challenge Mode",
            "Smart Alerts",
            lang === "cs" ? "Market Scanner + Kalendář" : "Market Scanner + Calendar",
            lang === "cs" ? "Prioritní AI" : "Priority AI",
          ]} cta={t("landing.cta.upgrade")} />
        </div>
      </section>

      <Seo
        title="TradeMind AI — AI mentor pro tradery | Prop trading, obchodní deník a analýza grafů"
        description="AI asistent pro prop trading a retail tradery. Analýza grafů ze screenshotu (BUY/SELL/WAIT, SL, TP), obchodní deník, risk management a Prop Firm Guardian pro prop firm challenge. Začni zdarma."
        canonical="https://trademind.cz/"
        schema={LANDING_SCHEMA}
      />
      <footer className="border-t border-zinc-800 py-12 mt-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <img src="/icons/icon-192.png" alt="TradeMind AI" className="w-7 h-7 rounded-md" />
                <span className="font-semibold tracking-tight text-zinc-200">TradeMind <span className="text-blue-500">AI</span></span>
              </div>
              <div className="flex items-center gap-2.5" data-testid="footer-contact-person">
                <img src="/author.webp" alt="Vratislav Výšek — zakladatel TradeMind AI" className="w-9 h-9 rounded-full object-cover border-2 border-amber-500/60" loading="lazy" />
                <span className="text-xs text-zinc-400 leading-tight">Vratislav Výšek<br /><span className="text-zinc-600">{lang === "cs" ? "Zakladatel & CEO · VV Software" : "Founder & CEO · VV Software"}</span></span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-zinc-200 transition-colors">{lang === "cs" ? "Soukromí" : "Privacy"}</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-zinc-200 transition-colors">{lang === "cs" ? "Podmínky" : "Terms"}</Link>
            <Link to="/blog" data-testid="footer-blog" className="hover:text-zinc-200 transition-colors">{lang === "cs" ? "Novinky" : "News"}</Link>
            <a href="mailto:vvsoftware@seznam.cz" className="hover:text-zinc-200 transition-colors">{lang === "cs" ? "Kontakt" : "Contact"}</a>
            <a href="tel:+420702995353" data-testid="footer-phone" className="whitespace-nowrap hover:text-zinc-200 transition-colors">+420 702 995 353</a>
          </div>
          </div>
          <p className="text-xs text-zinc-500 max-w-3xl leading-relaxed" data-testid="footer-faq-promo">
            <strong className="text-zinc-400">{lang === "cs" ? "Jak uplatním slevový kód?" : "How do I redeem a discount code?"}</strong>{" "}
            {lang === "cs"
              ? "Při výběru předplatného zadej kód START10 do pole pro slevový kód a získáš 10% slevu na první 3 měsíce."
              : "When subscribing, enter code START10 in the discount code field at checkout to get 10% off your first 3 months."}
          </p>
          <p className="text-xs text-zinc-500 max-w-3xl leading-relaxed">{t("landing.footer")}<br />
            <span data-testid="footer-operator">Provozovatel: Vratislav Výšek (VV Software), IČO: 30038758, Nové sady 988/2, 602 00 Brno. Fyzická osoba zapsaná v živnostenském rejstříku. Neplátce DPH.</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
